import { css, Rule } from 'glamor'
import * as React from 'react'
import * as PropTypes from 'prop-types'

import getStyles from './styles'

import { isKey } from '../../utils'
import { useTheme } from '../../theme'
import { UiVariant } from '../../types'

type CardVariant = UiVariant | 'white' | 'content'

const variants: CardVariant[] = [
  'cta',
  'primary',
  'secondary',
  'negative',
  'positive',
  'alert',
  'brand',
  'white',
  'content',
]
type CardBackground =
  | 'main'
  | 'gray'
  | 'content'
  | 'cta'
  | 'primary'
  | 'secondary'
  | 'negative'
  | 'positive'
  | 'alert'
  | 'brand'
  | 'overlay'
  | 'overlayLight'

const backgrounds: CardBackground[] = [
  'main',
  'gray',
  'content',
  'cta',
  'primary',
  'secondary',
  'negative',
  'positive',
  'alert',
  'brand',
  'overlay',
  'overlayLight',
]

interface ICardProps {
  children: React.ReactNode
  onClick?: () => void
  flush?: boolean
  border?: boolean
  style?: Rule
  tight?: boolean
  hoverable?: boolean
  variant?: CardVariant
}

/**
 * Card
 *
 * Put your children in a card
 *
 * @type React.Component
 *
 * @example
   import { Card } from '../../ui-kit'
   <Card onClick={() => console.warn('Wow, this is clickable'):
     My Children in a Card
   </Card>
 */

/* eslint-disable jsx-a11y/no-static-element-interactions */
export function Card({
  children,
  onClick,
  style,
  flush,
  border,
  tight,
  hoverable,
  variant,
  ...additional
}: ICardProps) {
  const styles = getStyles(useTheme())
  const clickableProps = onClick ? { onClick, role: 'presentation' } : null

  return (
    <section
      {...css([
        styles.card.base,
        isKey(styles.card.variants, variant) && styles.card.variants[variant],
        !border && styles.card.borderless,
        tight && styles.card.tight,
        (onClick || hoverable) && styles.card.hoverable,
        flush && styles.card.flush,
        style,
      ])}
      {...clickableProps}
      {...additional}
    >
      {children}
    </section>
  )
}

Card.propTypes = {
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  flush: PropTypes.bool,
  border: PropTypes.bool,
  style: PropTypes.object,
  tight: PropTypes.bool,
  hoverable: PropTypes.bool,
  variant: PropTypes.oneOf(variants),
}

Card.defaultProps = {
  variant: 'white' as CardVariant,
  border: true,
}

Card.VARIANTS = variants

interface ICardHeaderProps {
  children: React.ReactNode
  component?: string | React.ComponentType<{}>
  flush?: boolean
  tight?: boolean
  style?: Rule
  border?: boolean
  background?: CardBackground
}

export function CardHeader({
  component: Component = 'header',
  children,
  flush,
  tight,
  border,
  background,
  style,
  ...additional
}: ICardHeaderProps) {
  const theme = useTheme()
  const styles = getStyles(theme)

  return (
    <Component
      {...css(
        styles.header.base,
        border && styles.header.border,
        flush && styles.header.flush,
        tight && styles.header.tight,
        tight && !border && styles.header.tightNoBorder,
        background && { backgroundColor: theme.colors.backgrounds[background] },
        style
      )}
      {...additional}
    >
      {React.isValidElement(children) ? children : <h1 {...css(styles.heading.base)}>{children}</h1>}
    </Component>
  )
}

CardHeader.propTypes = {
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  children: PropTypes.node.isRequired,
  flush: PropTypes.bool,
  tight: PropTypes.bool,
  style: PropTypes.object,
  border: PropTypes.bool,
  background: PropTypes.oneOf(backgrounds),
}

CardHeader.defaultProps = {
  border: true,
}

interface ICardContentProps {
  children: React.ReactNode
  flush?: boolean
  style?: Rule
  tight?: boolean
  background?: CardBackground
}

export function CardContent({ children, flush, style, tight, background, ...additional }: ICardContentProps) {
  const theme = useTheme()
  const styles = getStyles(theme)

  return (
    <div
      {...css(
        styles.content.base,
        flush && styles.content.flush,
        tight && styles.content.tight,
        background && { backgroundColor: theme.colors.backgrounds[background] },
        style
      )}
      {...additional}
    >
      {children}
    </div>
  )
}

CardContent.propTypes = {
  children: PropTypes.node.isRequired,
  flush: PropTypes.bool,
  style: PropTypes.object,
  tight: PropTypes.bool,
  background: PropTypes.oneOf(backgrounds),
}

interface ICardFooterProps {
  children: React.ReactNode
  flush?: boolean
  tight?: boolean
  style?: Rule
  border?: boolean
  background?: CardBackground
}

export function CardFooter({ children, flush, tight, border, style, background, ...additional }: ICardFooterProps) {
  const theme = useTheme()
  const styles = getStyles(theme)

  return (
    <footer
      {...css(
        styles.footer.base,
        border && styles.footer.border,
        flush && styles.footer.flush,
        tight && styles.footer.tight,
        tight && !border && styles.footer.tightNoBorder,
        background && { backgroundColor: theme.colors.backgrounds[background] },
        style
      )}
      {...additional}
    >
      {children}
    </footer>
  )
}

CardFooter.propTypes = {
  children: PropTypes.node.isRequired,
  flush: PropTypes.bool,
  tight: PropTypes.bool,
  style: PropTypes.object,
  border: PropTypes.bool,
  background: PropTypes.oneOf(backgrounds),
}

CardFooter.defaultProps = {
  border: true,
}
